import styled from '@emotion/styled';
import { Box, Button } from '@mui/material';
import {
    Route,
    Routes,
    useNavigate,
    useParams,
    useLocation
} from 'react-router-dom';
import { ReactNode, useEffect, useMemo } from 'react';
import { grey } from '@mui/material/colors';
import { staticColors } from '@/lib/theme';
import { TextMedium } from '@/typography/Typography';

const SwitchSelect = styled.div`
    margin-top: ${(props: any) => props.theme.spacing(2)};
    display: flex;
    position: relative;
    button:not(:last-child) {
        margin-right: 10px;
    }
`;

interface BadgeProps {
    selected?: boolean;
}

const Badge = styled.div<BadgeProps>`
    background: ${(props: any) =>
        props.selected ? props.theme.palette.primary.main : grey[100]};
    color: ${(props) => (props.selected ? 'white' : grey[700])};
    border-radius: 16px;
    padding: 0px 8px;
    height: 22px;
    /* width: 28px; */
    margin-left: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export interface SwitchItem {
    primaryText: string;
    badgeText: string;
    route: string;
    search?: string;
    path?: string;
    overrideTestPath?: string;
    routeComponent: ReactNode;
}

interface ViewSwitchProps {
    itemList: SwitchItem[];
    setSelectedIndex: (index: number | null) => void;
    selectedIndex: number | null;
}

export const ViewSwitch = ({
    itemList,
    setSelectedIndex,
    selectedIndex
}: ViewSwitchProps) => {
    const selectedStyle = (index: number) =>
        index == selectedIndex
            ? {
                  zIndex: 10
              }
            : {
                  color: 'rgba(0, 0, 0, 0.54)', //TDOD: change
                  borderBottom: '0px'
              };

    const navigate = useNavigate();
    const location = useLocation();

    const params = useParams();

    // Helper function to find the matching tab index for current route
    const findMatchingTabIndex = (pathname: string): number => {
        let matchedIndex = 0;

        itemList?.forEach((el, index) => {
            const regex = new RegExp(el.route);
            let isMatch = regex.test(pathname);

            if (el.overrideTestPath) {
                isMatch = pathname.indexOf(el.overrideTestPath) > -1;
            }

            if (isMatch) {
                matchedIndex = index;
            }
        });

        return matchedIndex;
    };

    // Effect to sync selectedIndex with current route
    useEffect(() => {
        if (itemList && itemList.length > 0) {
            const matchedIndex = findMatchingTabIndex(location.pathname);

            // Only update if different to prevent unnecessary re-renders
            if (matchedIndex !== selectedIndex) {
                setSelectedIndex(matchedIndex);
            }
        }
    }, [location.pathname, itemList.length, selectedIndex, setSelectedIndex]);

    // Effect to handle initial navigation to first tab
    useEffect(() => {
        if (itemList && itemList.length > 0) {
            // If we're at the parent route with no sub-route, navigate to first tab
            if ('' == params['*'] || params['*'] == undefined) {
                const pathSegments = location.pathname
                    .split('/')
                    .filter((segment) => segment !== '');
                const parentPath = pathSegments.join('/');
                const firstRoute = itemList[0]?.route;
                if (firstRoute) {
                    const searchString = location.search;
                    const targetPath = `/${parentPath}/${firstRoute}${searchString}`;
                    // Only navigate if not already at the target
                    if (location.pathname !== targetPath.split('?')[0]) {
                        navigate(targetPath, { replace: true });
                    }
                }
            }
        }
    }, [itemList.length, params, location.pathname, navigate]);

    const changeView = (index: number) => {
        const selectedItem = itemList[index];

        // Don't update selectedIndex here - let the route sync effect handle it
        // This prevents the visual flash from double updates

        // Find the common parent path for all tabs
        const pathSegments = location.pathname
            .split('/')
            .filter((segment) => segment !== '');

        let parentPathSegments: string[] = [];

        // Determine the parent path based on current location
        if (pathSegments.length === 1) {
            // We're at a top-level route like /settings
            // Parent is the current segment itself
            parentPathSegments = pathSegments;
        } else if (pathSegments.includes('page')) {
            // Handle pagination: /users/userId/user-transactions/page/2
            // Find the route pattern and extract everything before it
            const pageIndex = pathSegments.indexOf('page');
            // Get the route base (e.g., 'user-transactions' from 'user-transactions/page/0')
            const selectedRouteBase = selectedItem.route.split('/')[0];

            // Find where the current route starts in the path
            let routeStartIndex = -1;
            for (let i = 0; i < pageIndex; i++) {
                if (
                    pathSegments[i] === selectedRouteBase ||
                    pathSegments
                        .slice(i)
                        .join('/')
                        .startsWith(selectedRouteBase)
                ) {
                    routeStartIndex = i;
                    break;
                }
            }

            if (routeStartIndex > 0) {
                // Everything before the route is the parent
                parentPathSegments = pathSegments.slice(0, routeStartIndex);
            } else {
                // Fallback: everything before 'page' minus one segment
                parentPathSegments = pathSegments.slice(0, pageIndex - 1);
            }
        } else {
            // Regular nested route: /users/userId/tab-name
            // Check if the last segment is one of our tab routes
            const lastSegment = pathSegments[pathSegments.length - 1];
            const routeBases = itemList.map((item) => item.route.split('/')[0]);

            // If the last segment matches a route base, remove it to get parent
            if (routeBases.includes(lastSegment)) {
                parentPathSegments = pathSegments.slice(0, -1);
            } else {
                // Check if any route base is in the path (for multi-segment routes)
                let foundRouteIndex = -1;
                for (let i = pathSegments.length - 1; i >= 0; i--) {
                    if (routeBases.includes(pathSegments[i])) {
                        foundRouteIndex = i;
                        break;
                    }
                }

                if (foundRouteIndex > 0) {
                    parentPathSegments = pathSegments.slice(0, foundRouteIndex);
                } else {
                    // No tab route found in path - we're at the base route
                    // Keep all segments as the parent (e.g., /users/{userId})
                    parentPathSegments = pathSegments;
                }
            }
        }

        const parentPath = parentPathSegments.join('/');

        // Only navigate if we're actually changing routes
        // Preserve search params when navigating
        const searchString = location.search;
        const targetPath = `/${parentPath}/${selectedItem?.route}${searchString}`;
        if (location.pathname !== targetPath.split('?')[0]) {
            navigate(targetPath, { replace: true });
        }
    };

    const getBadgeText = (el: SwitchItem, selected: boolean) => {
        let badgeText = el.badgeText;
        if (+el.badgeText && parseInt(el.badgeText) > 100) {
            badgeText = '99+';
        }
        return <Badge selected={selected}>{badgeText}</Badge>;
    };

    const rendered = useMemo(() => {
        return (
            <>
                {itemList && (
                    <>
                        {' '}
                        <SwitchSelect>
                            {itemList.map((el, index) => (
                                <Button
                                    variant="outlined"
                                    key={index}
                                    onClick={() => changeView(index)}
                                    style={{
                                        border: 'none',
                                        borderBottom: '2px solid',
                                        borderRadius: '10px 10px 0px 0px',
                                        textTransform: 'capitalize',
                                        ...selectedStyle(index)
                                    }}
                                >
                                    <TextMedium>{el.primaryText}</TextMedium>
                                    {el.badgeText && (
                                        <Badge
                                            selected={index == selectedIndex}
                                        >
                                            {el.badgeText}
                                        </Badge>
                                    )}
                                </Button>
                            ))}
                            <div
                                style={{
                                    height: '0.5px',
                                    width: '100%',
                                    background: staticColors.buttonGreyText,
                                    opacity: 0.2,
                                    borderRadius: '2px',
                                    position: 'absolute',
                                    right: 0,
                                    zIndex: '9',
                                    bottom: '1px'
                                }}
                            ></div>
                        </SwitchSelect>
                        <Box m={3}></Box>
                        <Routes>
                            {itemList.map((el, index) => {
                                return (
                                    <Route
                                        key={index}
                                        path={el?.path ?? el?.route}
                                        element={el?.routeComponent}
                                    />
                                );
                            })}
                        </Routes>
                    </>
                )}
            </>
        );
    }, [selectedIndex, itemList]);

    return <>{rendered}</>;
};
